<template>
  <Modal
    size="md"
    @close="$emit('close')"
  >
    <template #header>
      Add purchase order
    </template>

    <template #body>
      <form
        class="flex flex-col space-y-4"
        @submit.prevent="submit"
      >
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Date <span class="text-red-500">*</span></span>

          <AppInput
            v-model="purchaseOrderForm.form.date"
            class="form-input mt-1"
            type="date"
            name="date"
            :error="purchaseOrderForm.getErrors('date')"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">Description <span class="text-red-500">*</span></span>

          <textarea
            v-model="purchaseOrderForm.form.description"
            name="description"
            rows="3"
            class="form-input resize-none mt-1"
          />

          <p
            v-if="purchaseOrderForm.hasErrors('description')"
            class="text-sm text-red-600 mt-1"
          >
            {{ purchaseOrderForm.getErrors('description') }}
          </p>
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">Amount <span class="text-red-500">*</span></span>

          <AppCurrencyInput
            v-model="purchaseOrderForm.form.amount"
            class="form-input mt-1"
            type="number"
            step="0.01"
            min="0"
            name="amount"
            :error="purchaseOrderForm.getErrors('amount')"
          />
        </label>

        <label class="block">
          <span class="text-sm font-medium text-gray-700">Contractor reference</span>

          <AppInput
            v-model="purchaseOrderForm.form.contractor_ref"
            class="form-input mt-1"
            type="text"
            name="contractor_ref"
            :error="purchaseOrderForm.getErrors('contractor_ref')"
          />
        </label>

        <div>
          <span class="text-sm font-medium text-gray-700">Attachments</span>

          <FileUpload
            v-model="purchaseOrderForm.form.images"
            class="mt-1"
            multiple
            @change="addFile"
          />

          <div
            v-if="filesUploaded.length"
            class="mt-3 flex flex-wrap gap-2"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="purchaseOrderForm.getErrors(`images.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </div>
      </form>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          Cancel
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createAction.is(States.LOADING)"
          @click="submit"
        >
          {{ createAction.is(States.LOADING) ? 'Saving...' : 'Create' }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput } from '@/helpers';
import { usePackagesStore } from '@/store/packages';
import { usePurchaseOrdersStore } from '@/store/purchaseOrders';
import { computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  projectId: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'created'): void;
}>();

const purchaseOrdersStore = usePurchaseOrdersStore();
const packagesStore = usePackagesStore();
const createAction = useStoreApiAction(purchaseOrdersStore.createPurchaseOrder);
const packages = computed(() => packagesStore.models.where('project_id', parseInt(props.projectId)).get());

const purchaseOrderForm = useFormData({
  date: dateForDateInput(new Date()) ?? '',
  description: '',
  amount: undefined as number | undefined,
  contractor_ref: '',
  images: [],
});

onMounted(() => {
  packagesStore.index(props.projectId);
});

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (_file, index) => {
  purchaseOrderForm.form.images.splice(index, 1);
});

const submit = () => {
  createAction.request(props.projectId, purchaseOrderForm.toFormDataObject()).then(() => {
    toast.success('Purchase order created');
    emit('close');
    emit('created');
  }).catch((error) => {
    purchaseOrderForm.setErrors(error.data);
  });
};
</script>
